<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useSwitchOrganisation } from "@/queries/orgs";
import { useSessionStore } from "@/stores/session-store";
import { useAlertStore } from "@/stores/alerts-store";

const props = defineProps<{
  show: boolean;
  targetOrganization: {
    id: string;
    name: string;
  };
  targetRoute: {
    name: string;
    params?: Record<string, string>;
    query?: Record<string, string>;
  };
  context?: string;
}>();

const emit = defineEmits<{
  "update:show": [value: boolean];
  confirmed: [data: { organizationId: string; route: any }];
  cancelled: [];
}>();

const router = useRouter();
const queryClient = useQueryClient();
const sessionStore = useSessionStore();
const { switchOrganisationAsync, switchOrganisationPending: isLoading } = useSwitchOrganisation();

const modalTitle = computed(() => {
  switch (props.context) {
    case "activity":
      return "Switch Organization - View Activity";
    case "issue":
      return "Switch Organization - Fix Issue";
    default:
      return "Switch Organization";
  }
});

const modalMessage = computed(() => {
  const orgName = props.targetOrganization?.name || "selected organization";
  switch (props.context) {
    case "activity":
      return `You're about to switch to ${orgName} to view this activity. Your current context will be changed.`;
    case "issue":
      return `You're about to switch to ${orgName} to address this issue. Your current context will be changed.`;
    default:
      return `You're about to switch to ${orgName}. Your current context will be changed.`;
  }
});

async function handleConfirm() {
  if (!props.targetOrganization?.id) return;

  try {
    await switchOrganisationAsync(props.targetOrganization.id);

    // Mirror the canonical switch in App.vue: refresh the session (so the header org + capability
    // gates update) and invalidate every cached query (most query keys don't include orgId, so they
    // would otherwise keep serving the previous org's data). Without this the switch only "takes"
    // after a hard reload.
    await sessionStore.syncSession();
    await queryClient.invalidateQueries();

    useAlertStore().addInfo(
      "Switched Organisation",
      `Switched to ${sessionStore.session.currentOrgName}`,
    );

    emit("confirmed", {
      organizationId: props.targetOrganization.id,
      route: props.targetRoute,
    });
    emit("update:show", false);

    if (props.targetRoute) {
      await router.push(props.targetRoute);
    }
  } catch (error) {
    console.error("Failed to switch organization:", error);
  }
}

function handleCancel() {
  emit("cancelled");
  emit("update:show", false);
}
</script>

<template>
  <div
    v-if="show"
    class="modal fade show d-block"
    tabindex="-1"
    @click.self="handleCancel"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-arrow-left-right me-2"></i>
            {{ modalTitle }}
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="handleCancel"
            :disabled="isLoading"
          ></button>
        </div>

        <div class="modal-body">
          <div class="d-flex align-items-start gap-3">
            <div class="text-warning">
              <i class="bi bi-exclamation-triangle-fill fs-4"></i>
            </div>
            <div>
              <p class="mb-2">{{ modalMessage }}</p>
              <div class="alert alert-info small mb-0">
                <i class="bi bi-info-circle me-1"></i>
                This will switch your current organization context and may
                affect other open tabs.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="handleCancel"
            :disabled="isLoading"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="handleConfirm"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-arrow-left-right me-2"></i>
            {{ isLoading ? "Switching..." : "Switch & Continue" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show" @click="handleCancel"></div>
</template>

<style scoped lang="scss">
/* COMMENTED OUT: Breaks Bootstrap modal behavior - use .show class instead */
/*.modal {*/
/*  display: block !important;*/
/*}*/
</style>
